import "core-js/modules/es.array.map.js";
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Box from 'components/Box';
import Flex from 'components/Flex';
import Button from 'components/Button';
import useIsElementInView from 'hooks/useIsElementInView';
var Layout = styled.div.withConfig({
  displayName: "ScrollableNav__Layout",
  componentId: "sc-9d1vvp-0"
})(["display:grid;grid-template-columns:min-content 1fr min-content;align-items:center;"]);

/**
 * The mask-image in this component consists of 2 or 3 separate
 * images (gradients) and creates a fade-out effect at the overflowing
 * edges.
 *
 * The left and right gradients are going from white at the edges to
 * transparent on the inside. The transparent gap in the middle is then
 * filled by the `baseGradient`, which is actually just a solid black area.
 *
 * The result is a white > black > white gradient (when both sides are
 * being faded out).
 *
 * Note the use of `mask-composite: exclude`, which allows the white areas
 * to become transparent, effectively inverting the mask.
 * (Without this, it'd be much harder to combine the different gradients
 * for this effect and we'd have to use one large gradient instead.)
 */

var baseGradient = 'linear-gradient(to right, black 0, black 100%)';
var leftGradient = 'linear-gradient(to right, white 1.5rem, transparent 4rem)';
var rightGradient = 'linear-gradient(to left, white 1.5rem, transparent 4rem)';
var Scroller = styled(Box).withConfig({
  displayName: "ScrollableNav__Scroller",
  componentId: "sc-9d1vvp-1"
})(["grid-row:1;grid-column:1 / -1;display:flex;overflow-x:auto;white-space:nowrap;scroll-behavior:smooth;&::-webkit-scrollbar{display:none;}mask-composite:exclude;&[data-mask-position='both']{mask-image:", ",", ",", ";}&[data-mask-position='left']{mask-image:", ",", ";}&[data-mask-position='right']{mask-image:", ",", ";}"], baseGradient, leftGradient, rightGradient, baseGradient, leftGradient, baseGradient, rightGradient);
var ArrowButton = function ArrowButton(_ref) {
  var direction = _ref.direction,
    onClick = _ref.onClick;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var icon = direction === 'left' ? 'arrow-left' : 'arrow-right';
  var gridColumn = direction === 'left' ? '1' : '-1';
  return /*#__PURE__*/React.createElement(Box, {
    gridRow: "1",
    gridColumn: gridColumn,
    z: 2
  }, /*#__PURE__*/React.createElement(Button, {
    iconOnly: true,
    icon: icon,
    size: "small",
    variant: "transparent",
    onClick: onClick
  }, t("actions_scroll_".concat(direction))));
};
function ScrollableNav(_ref2) {
  var _ref2$spacing = _ref2.spacing,
    spacing = _ref2$spacing === void 0 ? 'm' : _ref2$spacing,
    _ref2$scrollAmount = _ref2.scrollAmount,
    scrollAmount = _ref2$scrollAmount === void 0 ? 150 : _ref2$scrollAmount,
    navigationLabel = _ref2.navigationLabel,
    children = _ref2.children;
  var scrollerRef = useRef(null);
  function scroll(direction) {
    if (scrollerRef.current) {
      scrollerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }
  var _useIsElementInView = useIsElementInView({
      initialValue: true
    }),
    isLeftPixelInView = _useIsElementInView.isInView,
    leftPixelRef = _useIsElementInView.watchedElementRef;
  var _useIsElementInView2 = useIsElementInView({
      initialValue: true
    }),
    isRightPixelInView = _useIsElementInView2.isInView,
    rightPixelRef = _useIsElementInView2.watchedElementRef;
  var isOverflowingLeft = !isLeftPixelInView;
  var isOverflowingRight = !isRightPixelInView;
  var maskPosition;
  if (isOverflowingLeft && isOverflowingRight) {
    maskPosition = 'both';
  } else if (isOverflowingLeft) {
    maskPosition = 'left';
  } else if (isOverflowingRight) {
    maskPosition = 'right';
  }
  return /*#__PURE__*/React.createElement(Layout, null, isOverflowingLeft && /*#__PURE__*/React.createElement(ArrowButton, {
    direction: "left",
    onClick: function onClick() {
      return scroll('left');
    }
  }), /*#__PURE__*/React.createElement(Scroller, {
    as: "nav",
    "aria-label": navigationLabel,
    ref: scrollerRef,
    "data-mask-position": maskPosition
  }, /*#__PURE__*/React.createElement(Box, {
    width: "1px",
    height: "1px",
    mr: "-1px",
    ref: leftPixelRef
  }), /*#__PURE__*/React.createElement(Flex, {
    as: "ul",
    display: "inline-flex",
    gap: spacing
  }, React.Children.map(children, function (child, index) {
    if (!child) return null;
    return /*#__PURE__*/React.createElement("li", {
      key: index
    }, child);
  })), /*#__PURE__*/React.createElement(Box, {
    width: "1px",
    height: "1px",
    ml: "-1px",
    ref: rightPixelRef
  })), isOverflowingRight && /*#__PURE__*/React.createElement(ArrowButton, {
    direction: "right",
    onClick: function onClick() {
      return scroll('right');
    }
  }));
}
export default ScrollableNav;